import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';

const { FiArrowLeft, FiStar, FiCalendar, FiMap, FiCamera } = FiIcons;

const festivalHighlights = [
  {
    title: "The Iconic Kiping",
    description: "Leaf-shaped rice wafers in vibrant colors that decorate the facades of every home, later grilled or fried as a local snack.",
    image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/3a0b3bdff92d42298e51a1aa90f67e00.png"
  },
  {
    title: "Grand Procession",
    description: "A solemn yet colorful parade of the image of San Isidro Labrador, accompanied by marching bands and costumed locals.",
    image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/44eb78f00d167eaa2605ba09913d9143.png"
  },
  {
    title: "Anok & Giant Figures",
    description: "Towering agricultural displays and giant paper-mâché figures (higantes) that represent various local trades and folklore.",
    image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/c754eb1480dc221e5fcabe5953c2d12e.jpg"
  },
  {
    title: "Decorated Heritage Homes",
    description: "During Pahiyas, Lucban’s homes are transformed into colorful works of art, decorated with kiping, fruits, vegetables, flowers, and native materials as a joyful expression of thanksgiving.",
    image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/8e2eec48843e3aad2a511ccb626c532f.png"
  },
  {
    title: "Harvest Displays",
    description: "Fresh produce, grains, fruits, and vegetables are beautifully arranged on homes and streets to celebrate abundance and gratitude for a bountiful harvest.",
    image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/8039a7bfe5e8ea6a1640b048be32e3fe.png"
  },
  {
    title: "Kiping Chandeliers",
    description: "Colorful kiping is often shaped into flowers, hanging ornaments, and chandelier-like decorations that bring brightness and charm to every corner of the festival.",
    image: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/391b9e3a8785af9ead2bcf7a8ffa8ef4.png"
  }
];

const travelTips = [
  { icon: FiCalendar, title: "Best Time to Arrive", text: "The main celebration is May 15, but festivities begin as early as May 1st. Arrive at least a day early." },
  { icon: FiMap, title: "The Walking Route", text: "Wear comfortable walking shoes. The decorated houses change every year, following a designated route." },
  { icon: FiCamera, title: "Golden Hour Photo Ops", text: "Early morning (7 AM) or late afternoon (4 PM) offers the best lighting for capturing colors." }
];

const PahiyasSpirit = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Pahiyas Spirit | R. Venue Apartelle Lucban",
    "description": "Experience the vibrant Pahiyas Festival in Lucban. Discover kiping decorations, grand processions, and the rich cultural heritage of Quezon's harvest celebration.",
    "url": "https://rvenueapartelle.lucban.ph/pahiyas-spirit"
  };

  return (
    <div className="w-full">
      <SEO 
        title="Pahiyas Spirit"
        description="Experience the vibrant Pahiyas Festival in Lucban. Discover kiping decorations, grand processions, and the rich cultural heritage of Quezon's harvest celebration."
        canonical="/pahiyas-spirit"
        ogImage="https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/0752927c6feffd15b82348e899a241bc.png"
        schema={pageSchema}
      />
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden pt-[100px]">
        <div className="absolute inset-0 z-0">
          <img src="https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvcG5n/0752927c6feffd15b82348e899a241bc.png" alt="Pahiyas Festival Colors" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-brand-olive/70" />
        </div>
        <div className="relative z-10 text-center px-6 mt-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-16 h-16 bg-brand-cream/10 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/20">
            <SafeIcon icon={FiStar} className="text-brand-cream text-2xl" />
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="font-script text-3xl text-brand-cream mb-4">A Celebration of Harvest</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em] text-white">Pahiyas Spirit</motion.h1>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 className="font-serif text-3xl uppercase tracking-widest text-brand-olive mb-8">Experience the Colors of Lucban</h2>
          <div className="w-20 h-[1px] bg-brand-olive/30 mx-auto mb-10" />
          <div className="space-y-6 text-brand-olive/80 text-lg leading-relaxed font-light text-left md:text-center">
            <p>Every 15th of May, the quiet town of Lucban transforms into a vibrant canvas of colors, creativity, and thanksgiving. The Pahiyas Festival is one of the Philippines' most spectacular cultural events.</p>
          </div>
        </div>
      </section>

      {/* Highlights Grid */}
      <section className="py-24 bg-white border-y border-brand-olive/5">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-4">Festival Highlights</h2>
            <div className="w-20 h-[1px] bg-brand-olive/20 mx-auto mt-6" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {festivalHighlights.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="group flex flex-col items-center text-center h-full">
                <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden mb-8 shadow-xl border-4 border-brand-cream relative">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                </div>
                <h3 className="font-serif text-xl uppercase tracking-widest text-brand-olive mb-4">{item.title}</h3>
                <p className="text-brand-olive/70 text-sm leading-relaxed font-light">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Guide Section */}
      <section className="py-24 bg-brand-olive text-white">
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="text-center mb-16">
            <p className="font-script text-3xl text-brand-cream mb-2">Visitor Guide</p>
            <h2 className="font-serif text-3xl uppercase tracking-widest text-white mb-4">Pro-Tips for the Festival</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {travelTips.map((tip, index) => (
              <div key={index} className="bg-white/5 border border-white/10 p-10 backdrop-blur-sm">
                <SafeIcon icon={tip.icon} className="text-brand-cream text-3xl mb-6" />
                <h4 className="font-serif text-lg uppercase tracking-widest text-brand-cream mb-4">{tip.title}</h4>
                <p className="text-white/70 text-sm leading-relaxed font-light">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-brand-cream text-center">
        <div className="container mx-auto px-6">
          <p className="text-brand-olive/80 text-lg mb-12 font-light max-w-2xl mx-auto">At R. Venue Apartelle, you are perfectly positioned in the heart of town to immerse yourself fully in this festive spirit.</p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6">
            <Link to="/rates" className="btn-primary">Book Your Festival Stay</Link>
            <Link to="/" className="inline-flex items-center gap-2 text-brand-olive font-bold uppercase tracking-widest text-xs hover:text-brand-dark transition-colors"><SafeIcon icon={FiArrowLeft} /> Back to Home</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PahiyasSpirit;